import React, { useState, useEffect } from 'react'
import { Bell, Check, CheckCheck, X } from 'lucide-react'
import { apiService } from '../lib/api'
import { Button } from './ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { formatDistanceToNow } from 'date-fns'

interface Notification {
  id: string
  type: string
  title: string
  body?: string
  isRead: boolean
  createdAt: string
}

export function NotificationDropdown() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch notifications and count
  const fetchNotifications = async () => {
    try {
      const [notifs, count] = await Promise.all([
        apiService.getNotifications({ limit: 10 }),
        apiService.getNotificationCount()
      ])
      setNotifications(notifs)
      setUnreadCount(count)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    }
  }

  // Fetch on mount and when dropdown opens
  useEffect(() => {
    fetchNotifications()
  }, [])

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchNotifications()
    }, 30000)

    return () => clearInterval(interval)
  }, [])

  // Refresh when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications()
    }
  }, [isOpen])

  const handleMarkAsRead = async (id: string) => {
    try {
      await apiService.markNotificationRead(id)
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, isRead: true } : n))
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
      // Force re-render to update styles
      setTimeout(() => {
        setNotifications(prev => [...prev])
      }, 0)
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      setIsLoading(true)
      await apiService.markAllNotificationsRead()
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getNotificationIcon = (type: string, isUnread: boolean = false) => {
    return <Bell className={`w-4 h-4 ${isUnread ? 'text-[#b8516f]' : 'text-muted-foreground'}`} />
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button 
          className="p-2 hover:bg-muted rounded-lg transition-colors relative text-foreground focus:outline-none focus:ring-2 focus:ring-[#742425]/50"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[1.15rem] h-[1.15rem] bg-[#742425] dark:bg-[#b8516f] text-white rounded-full flex items-center justify-center px-1 text-[10px] font-bold border-2 border-background shadow-sm">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[500px] overflow-y-auto bg-card text-card-foreground border-border shadow-lg">
        <DropdownMenuLabel className="flex items-center justify-between text-foreground">
          <span className="font-semibold">Notifications</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={isLoading}
              className="h-auto py-1 px-2 text-xs text-[#742425] dark:text-[#b8516f] hover:text-[#742425] dark:hover:text-[#b8516f] hover:bg-[#742425]/10 dark:hover:bg-[#742425]/20"
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-border" />
        
        {notifications.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <Bell className="w-12 h-12 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No notifications</p>
          </div>
        ) : (
          <div className="max-h-[400px] overflow-y-auto divide-y divide-border/50">
            {notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`flex flex-col items-start p-3 cursor-pointer outline-none transition-colors
                  ${!notification.isRead 
                    ? 'bg-[#742425]/10 dark:bg-[#742425]/20 hover:bg-[#742425]/15 dark:hover:bg-[#742425]/25 border-l-2 border-[#742425] dark:border-[#b8516f]' 
                    : 'hover:bg-muted/60 focus:bg-muted/60'
                  }`}
                onClick={() => !notification.isRead && handleMarkAsRead(notification.id)}
              >
                <div className="flex items-start justify-between w-full gap-2">
                  <div className="flex items-start gap-2.5 flex-1">
                    <div className="mt-0.5 shrink-0">
                      {getNotificationIcon(notification.type, !notification.isRead)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight text-foreground">
                        {notification.title}
                      </p>
                      {notification.body && (
                        <p className="text-xs mt-1 line-clamp-2 text-muted-foreground">
                          {notification.body}
                        </p>
                      )}
                      <p className="text-[11px] mt-1 text-muted-foreground/70">
                        {formatDistanceToNow(new Date(notification.createdAt), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                  </div>
                  {!notification.isRead && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleMarkAsRead(notification.id)
                      }}
                      className="p-1 hover:bg-[#742425]/20 text-[#742425] dark:text-[#b8516f] rounded transition-colors focus:outline-none"
                      title="Mark as read"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
